import type { ReserveLock, ReserveLockResponse } from './types';
import { dedupe } from './client';

const PATH = '/api/controls/reserve';

// Cookie-based auth (zelfde patroon als evHint.ts): credentials:'include' stuurt
// de zbs_session cookie automatisch mee. Backend accepteert ook X-API-Key voor
// scripts/curl, maar de browser-frontend gebruikt uitsluitend de cookie.

// App.tsx pollt deze lock en ControlsColumn leest 'm bij mount ook. Dat waren
// twee requests op hetzelfde moment naar dezelfde URL; `dedupe` maakt er één
// van zolang de eerste nog loopt. Response en foutvorm blijven identiek.
export async function fetchReserveLock(): Promise<ReserveLockResponse> {
  return dedupe(`GET ${PATH}`, async () => {
    const res = await fetch(PATH, {
      credentials: 'include',
      headers: { Accept: 'application/json' },
    });
    if (!res.ok) {
      let body = '';
      try { body = await res.text(); } catch { /* ignore */ }
      throw new Error(`GET ${PATH}: ${res.status} ${body.slice(0, 200)}`);
    }
    return await res.json() as ReserveLockResponse;
  });
}

export async function setReserveLock(socPct: number, hours: number): Promise<ReserveLock> {
  const res = await fetch(PATH, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
    body: JSON.stringify({ soc_pct: socPct, hours }),
  });
  if (!res.ok) {
    let detail = '';
    try {
      const j = await res.json() as { detail?: string };
      detail = j.detail ?? '';
    } catch {
      try { detail = await res.text(); } catch { /* ignore */ }
    }
    throw new Error(`POST ${PATH}: ${res.status} ${detail.slice(0, 200)}`);
  }
  const data = await res.json() as { lock: ReserveLock };
  return data.lock;
}

export async function cancelReserveLock(): Promise<void> {
  const res = await fetch(PATH, {
    method: 'DELETE',
    credentials: 'include',
    headers: { Accept: 'application/json' },
  });
  if (!res.ok) {
    let body = '';
    try { body = await res.text(); } catch { /* ignore */ }
    throw new Error(`DELETE ${PATH}: ${res.status} ${body.slice(0, 200)}`);
  }
}

/** Aantal ms tot lock.expires_at_utc; nooit negatief, 0 bij parse-fout. */
export function remainingMs(lock: ReserveLock, now: Date = new Date()): number {
  const expires = Date.parse(lock.expires_at_utc);
  if (Number.isNaN(expires)) return 0;
  const diff = expires - now.getTime();
  return diff > 0 ? diff : 0;
}
